import { createSelector } from "@reduxjs/toolkit";
import { useSelector } from "react-redux";

const selectEnvironment = state => state.environment;

/**
 * Given an environment path return the organization project and environment ID
 *
 * @param {string} path url path to be parsed
 * @returns [organizationId, projectId, environmentId]
 */
const extractEnvironmentInfoFromPath = path =>
  path?.split("/").slice(1, 4).map(decodeURIComponent) || [];

const selectPath = createSelector(
  () => window.location.pathname,
  extractEnvironmentInfoFromPath
);

const environmentFromRoute = createSelector(
  selectEnvironment,
  selectPath,
  (environment, path) => environment.data?.[path[0]]?.[path[1]]?.[path[2]]
);

export const getDeploymentType = createSelector(
  selectEnvironment,
  selectPath,
  (environment, path) => {
    const environmentData = environment.data?.[path[0]]?.[path[1]]?.[path[2]];
    const deployType =
      environmentData?._embedded?.deployments[0]?.type ||
      environmentData?._links?.["#metrics"]?.type; //Git 14

    // map deployment type to old metrics link type to determine which view to use
    // Todo: remove old types once all projects moved to git15+
    switch (deployType) {
      case "dedicated":
      case "dedication":
        return "dedication"; // aka dg3
      case "local":
      case "grid":
        return "grid";
      case "enterprise":
        return "dedicated"; // aka dg2
      default:
        return undefined;
    }
  }
);

const getChorusLink = createSelector(environmentFromRoute, environment =>
  getMetricsLink(environment?._links?.["#metrics"])
);

const getMetricsLink = links => {
  if (!(links instanceof Array)) {
    return false;
  }

  if (links.length < 1) {
    return false;
  }

  const [link] = links;

  if (!link.href || !link.collection) {
    return false;
  }

  return link;
};

/**
 * @typedef MetricsLink
 * @property {string} collection collection string
 * @property {string} href Chorus endpoint
 * @property {string} type Kind of backend (obsolete)
 * @property {string} deploymentType Kind of backend (Git 15+)
 * @property {Environment} environment Kind of backend (Git 15+)
 * @property {string} max_range Metrics maximum range
 */
/**
 * Gets the metrics link based on the current url.
 *
 * @returns {MetricsLink} the metrics link
 */
const useMetricsLinkData = () => {
  const environment = useSelector(
    environmentFromRoute,
    (prev, next) => prev?.id === next?.id
  );
  const link = useSelector(
    getChorusLink,
    (prev, next) => prev?.collection === next?.collection
  );

  const deploymentType = useSelector(getDeploymentType);

  if (link && deploymentType) {
    link.deploymentType = deploymentType;
  }
  return { ...link, environment };
};

export default useMetricsLinkData;
